import React from 'react';
import { Handle, Position } from 'reactflow';
import {
  Box,
  Flex,
  Text,
  IconButton,
  HStack,
  VStack,
  Tag,
} from '@chakra-ui/react';
import { FiEdit, FiCopy, FiTrash2, FiGitBranch } from 'react-icons/fi';

const GROUP_COLOR_MAP = {
  blue:   { bg: '#3B82F6', light: 'rgba(59,130,246,0.08)',  border: 'rgba(59,130,246,0.3)',  text: 'var(--brand-primary-hover)' },
  green:  { bg: '#10B981', light: 'rgba(16,185,129,0.08)',  border: 'rgba(16,185,129,0.3)',  text: '#065f46' },
  purple: { bg: '#8B5CF6', light: 'rgba(139,92,246,0.08)',  border: 'rgba(139,92,246,0.3)',  text: '#5b21b6' },
  orange: { bg: '#F59E0B', light: 'rgba(245,158,11,0.08)',  border: 'rgba(245,158,11,0.3)',  text: '#92400e' },
  pink:   { bg: '#EC4899', light: 'rgba(236,72,153,0.08)',  border: 'rgba(236,72,153,0.3)',  text: '#9d174d' },
  teal:   { bg: '#14B8A6', light: 'rgba(20,184,166,0.08)',  border: 'rgba(20,184,166,0.3)',  text: '#0f766e' },
};

const OPERATOR_LABELS = {
  equals: '=', not_equals: '≠', greater_than: '>',
  less_than: '<', greater_equal: '≥', less_equal: '≤',
  contains: '⊃', starts_with: '⊃*', not_contains: '⊄', ends_with: '⊃$',
  is_true: 'is true', is_false: 'is false', exists: '∃', not_exists: '∄',
};

const formatRule = (rule) => {
  const varName =
    rule.variablePath?.split('.').pop() ||
    rule.variable?.split('.').pop() ||
    'var';
  const op = OPERATOR_LABELS[rule.operator] || rule.operator;
  if (['is_true', 'is_false', 'exists', 'not_exists'].includes(rule.operator)) {
    return `${varName} ${op}`;
  }
  return `${varName} ${op} ${rule.value || '?'}`;
};

const getGroupRulesSummary = (group) => {
  const rules = group.rules || [];
  if (rules.length === 0) return null;
  if (rules.length === 1) return formatRule(rules[0]);
  if (rules.length === 2) return `${formatRule(rules[0])} AND ${formatRule(rules[1])}`;
  return `${formatRule(rules[0])} AND ${rules.length - 1} more...`;
};

const SmartConditionNode = ({ data }) => {
  const groups = Array.isArray(data.groups) && data.groups.length > 0 ? data.groups : [];
  const defaultLabel = data.defaultLabel || 'No Match';
  const totalRules = groups.reduce((acc, g) => acc + (g.rules?.length || 0), 0);

  const getColor = (colorName) => GROUP_COLOR_MAP[colorName] || GROUP_COLOR_MAP.blue;

  return (
    <Box
      bg="white"
      border="2px solid"
      borderColor="#8B5CF6"
      borderRadius="xl"
      boxShadow={data.selected ? 'xl' : 'lg'}
      minW="260px"
      position="relative"
    >
      <Handle
        type="target"
        position={Position.Left}
        style={{ background: '#8B5CF6' }}
      />

      {/* Header */}
      <Flex
        bg="#8B5CF6"
        color="white"
        px={4}
        py={2.5}
        borderTopRadius="xl"
        align="center"
        gap={2}
      >
        <FiGitBranch size={16} />
        <Box flex={1}>
          <Text noOfLines={1} fontSize="sm" fontWeight="medium" lineHeight="short">
            Smart Condition
          </Text>
          <Text fontSize="10px" opacity={0.85}>
            Group-Based • OR Logic
          </Text>
        </Box>
        <HStack spacing={1}>
          <IconButton
            icon={<FiEdit size={14} />}
            size="xs"
            variant="ghost"
            color="white"
            _hover={{ bg: 'whiteAlpha.300' }}
            onClick={(e) => { e.stopPropagation(); data.onEdit?.(data.id); }}
            aria-label="Edit"
          />
          <IconButton
            icon={<FiCopy size={14} />}
            size="xs"
            variant="ghost"
            color="white"
            _hover={{ bg: 'whiteAlpha.300' }}
            onClick={(e) => { e.stopPropagation(); data.onDuplicate?.(data.id); }}
            aria-label="Duplicate"
          />
          <IconButton
            icon={<FiTrash2 size={14} />}
            size="xs"
            variant="ghost"
            color="white"
            _hover={{ bg: 'whiteAlpha.300' }}
            onClick={(e) => { e.stopPropagation(); data.onDelete?.(data.id); }}
            aria-label="Delete"
          />
        </HStack>
      </Flex>

      {/* Body */}
      <Box p={3}>
        {/* Label */}
        <Text fontSize="sm" fontWeight="medium" mb={1} noOfLines={1}>
          {data.label || 'Smart Condition'}
        </Text>

        {/* Summary */}
        <Flex
          align="center"
          gap={2}
          fontSize="10px"
          color="purple.700"
          bg="purple.50"
          px={2}
          py={1}
          borderRadius="md"
          mb={3}
        >
          <FiGitBranch size={10} />
          <Text>
            {groups.length} Group{groups.length !== 1 ? 's' : ''} •{' '}
            {totalRules} Condition{totalRules !== 1 ? 's' : ''}
          </Text>
        </Flex>

        <VStack spacing={2} align="stretch">
          {/* Group rows */}
          {groups.length === 0 ? (
            <Box
              border="1px dashed"
              borderColor="gray.200"
              borderRadius="lg"
              p={3}
              textAlign="center"
            >
              <Text fontSize="xs" color="gray.400">No groups configured</Text>
            </Box>
          ) : (
            groups.map((group, index) => {
              const cc = getColor(group.color);
              const summary = getGroupRulesSummary(group);

              return (
                <Box
                  key={group.id}
                  border="1px solid"
                  borderColor={cc.border}
                  borderRadius="lg"
                  bg={cc.light}
                  p={2}
                  position="relative"
                >
                  <Flex align="center" gap={2} mb={summary ? 1 : 0}>
                    <Tag
                      fontSize="9px"
                      px={1.5}
                      py={0}
                      colorScheme={index === 0 ? 'purple' : 'orange'}
                      variant="subtle"
                    >
                      {index === 0 ? 'IF' : 'OR IF'}
                    </Tag>
                    <Text fontSize="xs" fontWeight="semibold" color={cc.text} flex={1} noOfLines={1}>
                      {group.label}
                    </Text>
                    <Text fontSize="10px" color="gray.500">
                      {group.rules?.length || 0} rule{(group.rules?.length || 0) !== 1 ? 's' : ''}
                    </Text>
                  </Flex>

                  {summary && (
                    <Text fontSize="10px" color="gray.600" noOfLines={2} mt={0.5}>
                      {summary}
                    </Text>
                  )}

                  <Handle
                    type="source"
                    position={Position.Right}
                    id={`group-${group.id}`}
                    // See flowHandles.css .branch-handle: -4px only cleared this row's
                    // own edge, landing well inside the node instead of on its border.
                    // -19px = row border (1px) + node body padding p={3} (12px) + node
                    // border (2px) + half the rendered 8px dot (6px forced width + this
                    // row's 1px white ring).
                    className="branch-handle"
                    style={{
                      width: 8,
                      height: 8,
                      background: cc.bg,
                      border: '1px solid white',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      '--flow-branch-offset': '-19px',
                    }}
                  />
                </Box>
              );
            })
          )}

          {/* Default / No Match */}
          <Box borderTop="1px solid" borderColor="gray.200" pt={2}>
            <Box
              border="1px solid"
              borderColor="gray.200"
              borderRadius="lg"
              bg="gray.50"
              p={2}
              position="relative"
            >
              <Flex align="center" gap={2}>
                <Box w={1.5} h={1.5} borderRadius="full" bg="gray.400" />
                <Text fontSize="xs" fontWeight="medium" color="gray.600">
                  {defaultLabel}
                </Text>
              </Flex>
              <Handle
                type="source"
                position={Position.Right}
                id="default"
                className="branch-handle"
                style={{
                  width: 8,
                  height: 8,
                  background: '#9CA3AF',
                  border: '1px solid white',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  '--flow-branch-offset': '-19px',
                }}
              />
            </Box>
          </Box>
        </VStack>
      </Box>
    </Box>
  );
};

export default SmartConditionNode;
